import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MaBottomNav, MaCircleButton, MaIcon, MaImageSlot, MaSectionHeader, MaTopBar,
} from "../components/ui";
import { Screen } from "../components/layout/Screen";
import { useEventSaves, useLists, useSaves, useVenues } from "../lib/queries";
import { TOTAL } from "../data/beenThere";
import { getChecked } from "../lib/beenThereStore";
import { formatEventDateShort } from "../lib/time";

function relTime(secs: number): string {
  const dt = Math.floor(Date.now() / 1000) - secs;
  if (dt < 3600) return "just now";
  if (dt < 86400) return `${Math.floor(dt / 3600)} hr ago`;
  const days = Math.floor(dt / 86400);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 31) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

export function Saved() {
  const navigate = useNavigate();
  const { data: lists = [] } = useLists();
  const { data: saves = [] } = useSaves();
  const { data: venues = [] } = useVenues();
  const { data: eventSaves = [] } = useEventSaves();

  const byId = useMemo(() => Object.fromEntries(venues.map((v) => [v.id, v])), [venues]);
  const wantToGo = saves.map((s) => byId[s.venue_id]).filter(Boolean);

  // Read checklist progress on mount; it's a localStorage read so we
  // don't need a query, but we re-read on every focus so the banner
  // updates after the user returns from /been-there.
  const [doneCount, setDoneCount] = useState<number>(() => getChecked().size);
  useEffect(() => {
    const onFocus = () => setDoneCount(getChecked().size);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  return (
    <Screen footer={<MaBottomNav active="saved" />}>
      <MaTopBar
        title="Your Lists"
        subtitle={`${saves.length} ${saves.length === 1 ? "venue" : "venues"} · ${eventSaves.length} ${eventSaves.length === 1 ? "event" : "events"} · ${lists.length} ${lists.length === 1 ? "list" : "lists"}`}
        trailing={<MaCircleButton icon="plus" dark />}
      />

      {/* Been there done that — entry card. Shown prominently on Saved
          because it's a personal-collection feature; the same card lives
          on /you. State is localStorage so works for anon too. */}
      <div style={{ padding: "0 16px 18px" }}>
        <button
          onClick={() => navigate("/been-there")}
          style={{
            display: "flex", alignItems: "center", gap: 14, width: "100%",
            padding: "14px 16px",
            background: "var(--ma-ink)", color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            cursor: "pointer", textAlign: "left",
            boxShadow: "0 6px 18px rgba(20,12,4,0.18)",
          }}
        >
          <div style={{
            width: 52, height: 52, flexShrink: 0,
            background: "var(--ma-brick)",
            border: "var(--ma-border) solid var(--ma-paper)",
            borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 26,
          }} aria-hidden>📌</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ma-mono" style={{
              fontSize: 9, letterSpacing: "0.18em",
              textTransform: "uppercase", color: "var(--ma-mustard)", marginBottom: 2,
            }}>
              your hit list
            </div>
            <div className="ma-display" style={{ fontSize: 20, color: "var(--ma-paper)", lineHeight: 1 }}>
              Been there, done that
            </div>
            <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-paper)", opacity: 0.7, marginTop: 4 }}>
              {doneCount} of {TOTAL} done · tap to check off more
            </div>
          </div>
          <MaIcon name="arrow" size={18} stroke={2} color="var(--ma-paper)" />
        </button>
      </div>

      {saves.length === 0 && lists.length === 0 && eventSaves.length === 0 && (
        <div style={{ padding: "0 16px 24px" }}>
          <div style={{
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            padding: 20, textAlign: "center",
          }}>
            <div className="ma-display" style={{ fontSize: 22, marginBottom: 6 }}>Nothing saved yet</div>
            <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)" }}>
              Tap the bookmark on any venue or event to save it.
            </div>
          </div>
        </div>
      )}

      {eventSaves.length > 0 && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label={`Saved events · ${eventSaves.length}`} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {eventSaves.map((e) => (
              <button
                key={e.id}
                onClick={() => navigate(`/event/${encodeURIComponent(e.id)}`)}
                style={{
                  display: "flex", gap: 12,
                  background: "var(--ma-cream)",
                  border: "var(--ma-border) solid var(--ma-ink)",
                  borderRadius: "var(--ma-card-radius)",
                  overflow: "hidden", padding: 0,
                  textAlign: "left", cursor: "pointer", color: "inherit", width: "100%",
                }}
              >
                <div style={{ width: 88, flexShrink: 0 }}>
                  <MaImageSlot
                    height={88}
                    tone={e.category === "music" ? "brick" : e.category === "art" ? "canal" : "mustard"}
                    src={e.imageUrl ?? null}
                    category={e.category ?? null}
                    fallbackInitial={e.title}
                  />
                </div>
                <div style={{ flex: 1, padding: "10px 12px 10px 0", minWidth: 0 }}>
                  <div style={{
                    fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 15,
                    color: "var(--ma-ink)", lineHeight: 1.15, marginBottom: 4,
                    display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden",
                  }}>
                    {e.title}
                  </div>
                  <div className="ma-mono" style={{
                    fontSize: 10, letterSpacing: "0.08em",
                    color: "var(--ma-ink-soft)",
                    display: "flex", gap: 8, flexWrap: "wrap",
                  }}>
                    {e.startsAt && (
                      <span style={{ color: "var(--ma-ink)" }}>
                        {formatEventDateShort(e.startsAt, e.endsAt ?? null)}
                      </span>
                    )}
                    <span>{e.category ?? ""}{e.neighborhood ? ` · ${e.neighborhood}` : ""}</span>
                  </div>
                </div>
                <div style={{ alignSelf: "center", paddingRight: 12 }}>
                  <MaIcon name="arrow" size={14} stroke={1.6} color="var(--ma-ink-soft)" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {wantToGo.length > 0 && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label={`Want to go · ${saves.length}`} />
          {/* Mirrors the saved-events row layout below — vertical rows
              of image + title + neighborhood. Was a horizontal-scroll
              of 132 px tiles which read as misaligned against the
              full-width event rows above. Same component shape now =
              perfectly aligned column. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {wantToGo.map((v) => (
              <button
                key={v.id}
                onClick={() => navigate(`/venue/${v.id}`)}
                style={{
                  display: "flex", gap: 12,
                  background: "var(--ma-cream)",
                  border: "var(--ma-border) solid var(--ma-ink)",
                  borderRadius: "var(--ma-card-radius)",
                  overflow: "hidden", padding: 0,
                  textAlign: "left", cursor: "pointer", color: "inherit", width: "100%",
                }}
              >
                <div style={{ width: 88, flexShrink: 0, position: "relative" }}>
                  <MaImageSlot
                    height={88}
                    tone={v.cat === "music" ? "brick" : "mustard"}
                    src={v.coverUrl}
                    category={v.cat}
                    fallbackInitial={v.name}
                  />
                  <div style={{
                    position: "absolute", top: 6, right: 6,
                    width: 22, height: 22, borderRadius: "50%",
                    background: "var(--ma-ink)", color: "var(--ma-paper)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <MaIcon name="bookmark" size={10} stroke={2.4} color="var(--ma-paper)" />
                  </div>
                </div>
                <div style={{ flex: 1, padding: "10px 12px 10px 0", minWidth: 0 }}>
                  <div style={{
                    fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 15,
                    color: "var(--ma-ink)", lineHeight: 1.15, marginBottom: 4,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {v.name}
                  </div>
                  <div className="ma-mono" style={{
                    fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-ink-soft)",
                  }}>
                    {v.cat}{v.neigh ? ` · ${v.neigh}` : ""}
                  </div>
                </div>
                <div style={{ alignSelf: "center", paddingRight: 12 }}>
                  <MaIcon name="arrow" size={14} stroke={1.6} color="var(--ma-ink-soft)" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {lists.length > 0 && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label={`Lists · ${lists.length}`} action="+ New list" />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {lists.map((l) => (
              <div key={l.id} style={{
                display: "flex", gap: 12,
                background: "var(--ma-cream)",
                border: "var(--ma-border) solid var(--ma-ink)",
                borderRadius: "var(--ma-card-radius)",
                padding: 12,
              }}>
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr",
                  gap: 2, width: 88, height: 88, flexShrink: 0,
                  border: "var(--ma-border) solid var(--ma-ink)",
                  background: "var(--ma-ink)", overflow: "hidden",
                }}>
                  {[0, 1, 2, 3].map((j) => (
                    <div key={j} style={{
                      background: `repeating-linear-gradient(${135 + j * 45}deg, var(--ma-${l.tone}) 0 4px, var(--ma-ink) 4px 5px)`,
                    }} />
                  ))}
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    <div className="ma-display" style={{ fontSize: 20, color: "var(--ma-ink)", lineHeight: 1.05 }}>
                      {l.name}
                    </div>
                    <div className="ma-mono" style={{
                      fontSize: 10, letterSpacing: "0.12em",
                      textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 3,
                    }}>
                      {l.count} {l.count === 1 ? "spot" : "spots"} · updated {relTime(l.updatedAt)}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    <MaIcon name="arrow" size={16} stroke={1.6} color="var(--ma-ink-soft)" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </Screen>
  );
}
